import React, { Component } from 'react';
import './Contact.css';
import logo from './logo.png';
import { Button, Input, Textarea, Field, Dropdown, Option } from '@fluentui/react-components';
import { 
  Navigation24Regular,
  SearchInfoFilled,
  OrganizationFilled,
  ScalesFilled,
  MailFilled,
  SearchFilled,
  PeopleFilled,
  BugFilled
} from '@fluentui/react-icons';
import NavBar from './NavBar';

interface TechnicalProps {}

interface TechnicalState {
  isMenuOpen: boolean;
  isProductsMenuOpen: boolean;
  isSolutionsMenuOpen: boolean;
  formData: {
    fullName: string;
    email: string;
    company: string;
    product: string;
    issueType: string;
    priority: string;
    teamsEnvironment: string;
    operatingSystem: string;
    teamsVersion: string;
    browser: string;
    tenantType: string;
    userPermission: string;
    usersAffected: string;
    issueTitle: string;
    issueDescription: string;
    stepsToReproduce: string;
    expectedBehavior: string;
    actualBehavior: string;
    errorMessages: string;
    additionalInfo: string;
  };
  isSubmitting: boolean;
  submitSuccess: boolean;
  submitError: string;
}

class Technical extends Component<TechnicalProps, TechnicalState> {
  constructor(props: TechnicalProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
      isProductsMenuOpen: false,
      isSolutionsMenuOpen: false,
      formData: {
        fullName: '',
        email: '',
        company: '',
        product: '',
        issueType: '',
        priority: '',
        teamsEnvironment: '',
        operatingSystem: '',
        teamsVersion: '',
        browser: '',
        tenantType: '',
        userPermission: '',
        usersAffected: '',
        issueTitle: '',
        issueDescription: '',
        stepsToReproduce: '',
        expectedBehavior: '',
        actualBehavior: '',
        errorMessages: '',
        additionalInfo: ''
      },
      isSubmitting: false,
      submitSuccess: false,
      submitError: ''
    };
  }

  toggleMenu = () => {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  toggleProductsMenu = () => {
    this.setState({ isProductsMenuOpen: !this.state.isProductsMenuOpen });
  };

  closeProductsMenu = () => {
    this.setState({ isProductsMenuOpen: false });
  };

  toggleSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: !this.state.isSolutionsMenuOpen });
  };

  closeSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: false });
  };

  handleInputChange = (field: keyof TechnicalState['formData'], value: string) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [field]: value
      }
    }));
  };

  handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    this.setState({ isSubmitting: true, submitError: '', submitSuccess: false });

    try {
      // TODO: Replace with your Power Automate webhook URL
      const powerAutomateUrl = 'https://default705c6e47bdf34fc09bad84965f7302.fd.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/7da663b8ef6b431cbf67b2362394e3d5/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=aI4nBgtQ7OYe-y6auNobg9_VUVj8xLF_rapoiW0fPdM';
      
      // Prepare data according to Power Automate schema
      const payload = {
        ...this.state.formData,
        submittedAt: new Date().toISOString()
      };
      
      // Send data to Power Automate
      const response = await fetch(powerAutomateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      // Show success message and reset form
      this.setState({ 
        submitSuccess: true, 
        isSubmitting: false,
        formData: {
          fullName: '',
          email: '',
          company: '',
          product: '',
          issueType: '',
          priority: '',
          teamsEnvironment: '',
          operatingSystem: '',
          teamsVersion: '',
          browser: '',
          tenantType: '',
          userPermission: '',
          usersAffected: '',
          issueTitle: '',
          issueDescription: '',
          stepsToReproduce: '',
          expectedBehavior: '',
          actualBehavior: '',
          errorMessages: '',
          additionalInfo: ''
        }
      });
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        this.setState({ submitSuccess: false });
      }, 5000);
      
    } catch (error) {
      console.error('Form submission error:', error);
      this.setState({ 
        submitError: 'An error occurred while submitting the support request. Please try again.',
        isSubmitting: false 
      });
    }
  };

  render() {
    const { formData, isSubmitting, submitSuccess, submitError } = this.state;

    return (
      <div className="contact-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="contact-hero-section">
          <div className="contact-hero-content">
            <h1 className="contact-hero-title">Technical Support</h1>
            <p className="contact-hero-subtitle">
              Need help with our Microsoft Teams apps? Fill out this detailed form to help us 
              diagnose and resolve your issue quickly.
            </p>
          </div>
        </section>

        {/* Technical Support Form Section */}
        <section className="contact-form-section">
          <div className="contact-container single-column">
            <div className="contact-form-wrapper">
              <div className="contact-form-header">
                <h2>Technical Support Request</h2>
                <p>Please provide as much detail as possible to help us resolve your issue efficiently.</p>
              </div>

              <form onSubmit={this.handleSubmit} className="contact-form">
                {/* Contact Information */}
                <div className="form-section-header">
                  <h3>Contact Information</h3>
                </div>

                <div className="form-row">
                  <Field label="Full Name" required className="form-field">
                    <Input 
                      value={formData.fullName}
                      onChange={(e) => this.handleInputChange('fullName', e.target.value)}
                      placeholder="Enter your full name"
                      required
                      size="large"
                    />
                  </Field>

                  <Field label="Email" required className="form-field">
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => this.handleInputChange('email', e.target.value)}
                      placeholder="your.email@company.com"
                      required
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Company" required className="form-field">
                    <Input 
                      value={formData.company}
                      onChange={(e) => this.handleInputChange('company', e.target.value)}
                      placeholder="Enter your company name"
                      required
                      size="large"
                    />
                  </Field>

                  <Field label="Product" required className="form-field">
                    <Dropdown
                      placeholder="Select a product"
                      value={formData.product}
                      selectedOptions={formData.product ? [formData.product] : []}
                      onOptionSelect={(_, data) => this.handleInputChange('product', data.optionValue as string)}
                      size="large"
                    >
                      <Option value="PShareEmail">PShareEmail</Option>
                      <Option value="DocSearch">DocSearch Tool</Option>
                      <Option value="SearchAssistant">Search Assistant</Option>
                      <Option value="pEntities">pEntities</Option>
                    </Dropdown>
                  </Field>
                </div>

                {/* Issue Details */}
                <div className="form-section-header">
                  <h3>Issue Details</h3>
                </div>

                <div className="form-row">
                  <Field label="Issue Type" required className="form-field">
                    <Dropdown
                      placeholder="Select issue type"
                      value={formData.issueType}
                      selectedOptions={formData.issueType ? [formData.issueType] : []}
                      onOptionSelect={(_, data) => this.handleInputChange('issueType', data.optionValue as string)}
                      size="large"
                    >
                      <Option value="Installation">Installation Issue</Option>
                      <Option value="Configuration">Configuration Issue</Option>
                      <Option value="Performance">Performance Issue</Option>
                      <Option value="Error">Error / Bug</Option>
                      <Option value="Authentication">Authentication Issue</Option>
                      <Option value="Permission">Permission Issue</Option>
                      <Option value="Integration">Integration Issue</Option>
                      <Option value="FeatureRequest">Feature Request</Option>
                      <Option value="Other">Other</Option>
                    </Dropdown>
                  </Field>

                  <Field label="Priority" required className="form-field">
                    <Dropdown
                      placeholder="Select priority"
                      value={formData.priority}
                      selectedOptions={formData.priority ? [formData.priority] : []}
                      onOptionSelect={(_, data) => this.handleInputChange('priority', data.optionValue as string)}
                      size="large"
                    >
                      <Option value="Low">Low - Minor inconvenience</Option>
                      <Option value="Medium">Medium - Functionality impaired</Option>
                      <Option value="High">High - Major functionality broken</Option>
                      <Option value="Critical">Critical - Service down</Option>
                    </Dropdown>
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Issue Title" required className="form-field full-width">
                    <Input 
                      value={formData.issueTitle}
                      onChange={(e) => this.handleInputChange('issueTitle', e.target.value)}
                      placeholder="Brief description of the issue"
                      required
                      size="large"
                    />
                  </Field>
                </div>

                {/* Environment Details */}
                <div className="form-section-header">
                  <h3>Environment Details</h3>
                </div>

                <div className="form-row">
                  <Field label="Teams Environment" required className="form-field">
                    <Dropdown
                      placeholder="Select environment"
                      value={formData.teamsEnvironment}
                      selectedOptions={formData.teamsEnvironment ? [formData.teamsEnvironment] : []}
                      onOptionSelect={(_, data) => this.handleInputChange('teamsEnvironment', data.optionValue as string)}
                      size="large"
                    >
                      <Option value="Desktop">Teams Desktop App</Option>
                      <Option value="Web">Teams Web Browser</Option>
                      <Option value="Mobile">Teams Mobile App</Option>
                      <Option value="Multiple">Multiple Platforms</Option>
                    </Dropdown>
                  </Field>

                  <Field label="Operating System" required className="form-field">
                    <Dropdown
                      placeholder="Select OS"
                      value={formData.operatingSystem}
                      selectedOptions={formData.operatingSystem ? [formData.operatingSystem] : []}
                      onOptionSelect={(_, data) => this.handleInputChange('operatingSystem', data.optionValue as string)}
                      size="large"
                    >
                      <Option value="Windows10">Windows 10</Option>
                      <Option value="Windows11">Windows 11</Option>
                      <Option value="macOS">macOS</Option>
                      <Option value="Linux">Linux</Option>
                      <Option value="iOS">iOS</Option>
                      <Option value="Android">Android</Option>
                      <Option value="Other">Other</Option>
                    </Dropdown>
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Teams Version" className="form-field">
                    <Input 
                      value={formData.teamsVersion}
                      onChange={(e) => this.handleInputChange('teamsVersion', e.target.value)}
                      placeholder="e.g., 1.6.00.4472 (check in Teams > Settings > About)"
                      size="large"
                    />
                  </Field>

                  <Field label="Browser (if web-based)" className="form-field">
                    <Dropdown
                      placeholder="Select browser"
                      value={formData.browser}
                      selectedOptions={formData.browser ? [formData.browser] : []}
                      onOptionSelect={(_, data) => this.handleInputChange('browser', data.optionValue as string)}
                      size="large"
                    >
                      <Option value="Chrome">Google Chrome</Option>
                      <Option value="Edge">Microsoft Edge</Option>
                      <Option value="Firefox">Mozilla Firefox</Option>
                      <Option value="Safari">Safari</Option>
                      <Option value="Other">Other</Option>
                      <Option value="NA">Not Applicable</Option>
                    </Dropdown>
                  </Field>
                </div>

                {/* Tenant & User Info */}
                <div className="form-section-header">
                  <h3>Tenant & User Information</h3>
                </div>

                <div className="form-row">
                  <Field label="Microsoft 365 Tenant Type" className="form-field">
                    <Dropdown
                      placeholder="Select tenant type"
                      value={formData.tenantType}
                      selectedOptions={formData.tenantType ? [formData.tenantType] : []}
                      onOptionSelect={(_, data) => this.handleInputChange('tenantType', data.optionValue as string)}
                      size="large"
                    >
                      <Option value="Free">Microsoft 365 Free</Option>
                      <Option value="BusinessBasic">Business Basic</Option>
                      <Option value="BusinessStandard">Business Standard</Option>
                      <Option value="BusinessPremium">Business Premium</Option>
                      <Option value="E3">Enterprise E3</Option>
                      <Option value="E5">Enterprise E5</Option>
                      <Option value="Education">Education</Option>
                      <Option value="Government">Government</Option>
                      <Option value="NotSure">Not Sure</Option>
                    </Dropdown>
                  </Field>

                  <Field label="User Permission Level" className="form-field">
                    <Dropdown
                      placeholder="Select permission level"
                      value={formData.userPermission}
                      selectedOptions={formData.userPermission ? [formData.userPermission] : []}
                      onOptionSelect={(_, data) => this.handleInputChange('userPermission', data.optionValue as string)}
                      size="large"
                    >
                      <Option value="GlobalAdmin">Global Administrator</Option>
                      <Option value="TeamsAdmin">Teams Administrator</Option>
                      <Option value="User">Standard User</Option>
                      <Option value="Guest">Guest User</Option>
                      <Option value="NotSure">Not Sure</Option>
                    </Dropdown>
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Number of Users Affected" className="form-field">
                    <Dropdown
                      placeholder="How many users are affected?"
                      value={formData.usersAffected}
                      selectedOptions={formData.usersAffected ? [formData.usersAffected] : []}
                      onOptionSelect={(_, data) => this.handleInputChange('usersAffected', data.optionValue as string)}
                      size="large"
                    >
                      <Option value="JustMe">Just me</Option>
                      <Option value="2-5">2-5 users</Option>
                      <Option value="6-20">6-20 users</Option>
                      <Option value="21-50">21-50 users</Option>
                      <Option value="50+">50+ users</Option>
                      <Option value="AllUsers">All users in organization</Option>
                    </Dropdown>
                  </Field>
                </div>

                {/* Detailed Description */}
                <div className="form-section-header">
                  <h3>Detailed Description</h3>
                </div>

                <div className="form-row">
                  <Field label="Issue Description" required className="form-field full-width">
                    <Textarea 
                      value={formData.issueDescription}
                      onChange={(e) => this.handleInputChange('issueDescription', e.target.value)}
                      placeholder="Describe the issue you're experiencing in detail..."
                      required
                      rows={4}
                      resize="vertical"
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Steps to Reproduce" className="form-field full-width">
                    <Textarea 
                      value={formData.stepsToReproduce}
                      onChange={(e) => this.handleInputChange('stepsToReproduce', e.target.value)}
                      placeholder="1. Open the app&#10;2. Click on...&#10;3. Enter...&#10;4. Observe the error"
                      rows={4}
                      resize="vertical"
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Expected Behavior" className="form-field full-width">
                    <Textarea 
                      value={formData.expectedBehavior}
                      onChange={(e) => this.handleInputChange('expectedBehavior', e.target.value)}
                      placeholder="What should happen when you perform the steps above?"
                      rows={3}
                      resize="vertical"
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Actual Behavior" className="form-field full-width">
                    <Textarea 
                      value={formData.actualBehavior}
                      onChange={(e) => this.handleInputChange('actualBehavior', e.target.value)}
                      placeholder="What actually happens? What goes wrong?"
                      rows={3}
                      resize="vertical"
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Error Messages" className="form-field full-width">
                    <Textarea 
                      value={formData.errorMessages}
                      onChange={(e) => this.handleInputChange('errorMessages', e.target.value)}
                      placeholder="Copy and paste any error messages you see (including error codes)"
                      rows={3}
                      resize="vertical"
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Additional Information" className="form-field full-width">
                    <Textarea 
                      value={formData.additionalInfo}
                      onChange={(e) => this.handleInputChange('additionalInfo', e.target.value)}
                      placeholder="Any other relevant information, workarounds tried, screenshots descriptions, etc."
                      rows={4}
                      resize="vertical"
                      size="large"
                    />
                  </Field>
                </div>

                {submitSuccess && (
                  <div className="contact-success-message">
                    <p>Your technical support request has been submitted successfully! Our team will review it and get back to you shortly.</p>
                  </div>
                )}

                {submitError && (
                  <div className="contact-error-message">
                    <p>{submitError}</p>
                  </div>
                )}

                <div className="form-actions">
                  <Button 
                    appearance="primary" 
                    size="large"
                    type="submit"
                    disabled={isSubmitting}
                    icon={<BugFilled />}
                    iconPosition="after"
                    className="submit-button"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Support Request'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default Technical;

